<!-- src/views/Wallet/Wallet.vue -->

<template>
  <div class="wallet-page">
    <!-- Hero Section -->
    <div class="hero-section">
      <div class="hero-content">
        <div class="hero-icon">
          <i class="fas fa-wallet"></i>
        </div>
        <h1 class="hero-title">{{ $t("wallet.title") }}</h1>
        <p class="hero-subtitle">{{ $t("wallet.subtitle") }}</p>
      </div>
      <div class="hero-decoration">
        <div class="circle circle-1"></div>
        <div class="circle circle-2"></div>
        <div class="circle circle-3"></div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="wallet-container">
      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <div class="loading-spinner">
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
        </div>
        <p class="loading-text">{{ $t("common.loading") }}</p>
      </div>

      <!-- Not Logged In State -->
      <div v-else-if="!isLoggedIn" class="login-prompt">
        <div class="login-card">
          <div class="login-icon">
            <i class="fas fa-user-lock"></i>
          </div>
          <h2>{{ $t("common.please_login_to_continue") }}</h2>
          <p class="login-desc">
            {{ $t("wallet.login_desc") || "سجل دخولك للوصول إلى محفظتك" }}
          </p>
          <a href="/login" class="login-btn">
            <i class="fas fa-sign-in-alt"></i>
            <span>{{ $t("common.login") }}</span>
          </a>
        </div>
      </div>

      <!-- Wallet Content -->
      <div v-else class="wallet-content">
        <!-- Balance Card -->
        <div class="balance-card">
          <div class="balance-card-bg">
            <div class="balance-pattern"></div>
          </div>
          <div class="balance-card-content">
            <div class="balance-header">
              <div class="balance-icon-wrapper">
                <img
                  :src="require('@/assets/wallet.png')"
                  alt="Wallet"
                  class="wallet-img"
                />
              </div>
              <div class="balance-info">
                <span class="balance-label">{{
                  $t("wallet.balance_label")
                }}</span>
                <div class="balance-amount">
                  <span v-if="loadingBalance" class="balance-loading">
                    <div class="mini-spinner"></div>
                  </span>
                  <span v-else class="amount-value">{{
                    walletBalanceDisplay
                  }}</span>
                </div>
              </div>
            </div>
            <div class="balance-actions">
              <button @click="showModal('payments-modal')" class="recharge-btn">
                <i class="fas fa-plus-circle"></i>
                <span>{{ $t("wallet.recharge") }}</span>
              </button>
              <button @click="showSendMoneyModal = true" class="send-money-btn">
                <i class="fas fa-exchange-alt"></i>
                <span>{{ $t("wallet.send_money") }}</span>
              </button>
              <button @click="showCouponModal = true" class="coupon-btn">
                <i class="fas fa-ticket-alt"></i>
                <span>{{ $t("wallet.coupon_btn") || "كوبون" }}</span>
              </button>
            </div>
          </div>
          <div class="card-shine"></div>
        </div>

        <!-- Tabbed Section -->
        <div class="wallet-tabs-section">
          <!-- Tabs Header -->
          <div class="tabs-header">
            <button
              class="tab-btn"
              :class="{ active: activeTab === 'transactions' }"
              @click="activeTab = 'transactions'"
            >
              <i class="fas fa-history"></i>
              <span>{{ $t("wallet.transactions_title") }}</span>
            </button>
            <button
              class="tab-btn"
              :class="{ active: activeTab === 'chargeRequests' }"
              @click="activeTab = 'chargeRequests'"
            >
              <i class="fas fa-file-invoice-dollar"></i>
              <span>{{ $t("wallet.charge_requests_title") }}</span>
            </button>
          </div>

          <!-- Tab Content -->
          <div class="tabs-content">
            <!-- Transactions Tab -->
            <div v-show="activeTab === 'transactions'" class="tab-pane">
              <!-- Transactions Loading -->
              <div v-if="transactionsLoading" class="transactions-loading">
                <div class="loading-spinner small">
                  <div class="spinner-ring"></div>
                  <div class="spinner-ring"></div>
                </div>
              </div>

              <!-- Transactions Empty -->
              <div
                v-else-if="transactions.length === 0"
                class="transactions-empty"
              >
                <div class="empty-icon">
                  <i class="fas fa-receipt"></i>
                </div>
                <p>{{ $t("wallet.no_transactions") }}</p>
              </div>

              <!-- Transactions List -->
              <div v-else class="transactions-list">
                <div
                  v-for="(tx, index) in transactions"
                  :key="index"
                  class="transaction-item"
                  :class="{ credit: tx.credit, debit: !tx.credit }"
                  :style="{ animationDelay: `${index * 0.05}s` }"
                >
                  <div class="tx-icon">
                    <i
                      :class="
                        tx.credit ? 'fas fa-arrow-up' : 'fas fa-arrow-down'
                      "
                    ></i>
                  </div>
                  <div class="tx-details">
                    <span class="tx-type">{{
                      tx.credit
                        ? $t("wallet.tx_type_credit")
                        : $t("wallet.tx_type_debit")
                    }}</span>
                    <span class="tx-description">{{ tx.details }}</span>
                  </div>
                  <div class="tx-meta">
                    <span class="tx-date">{{ tx.created_at }}</span>
                    <span class="tx-index">#{{ index + 1 }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Charge Requests Tab -->
            <div v-show="activeTab === 'chargeRequests'" class="tab-pane">
              <!-- Charge Requests Loading -->
              <div v-if="chargeRequestsLoading" class="transactions-loading">
                <div class="loading-spinner small">
                  <div class="spinner-ring"></div>
                  <div class="spinner-ring"></div>
                </div>
              </div>

              <!-- Charge Requests Empty -->
              <div
                v-else-if="chargeRequests.length === 0"
                class="transactions-empty"
              >
                <div class="empty-icon">
                  <i class="fas fa-file-invoice"></i>
                </div>
                <p>{{ $t("wallet.no_charge_requests") }}</p>
              </div>

              <!-- Charge Requests List -->
              <div v-else class="charge-requests-list">
                <div
                  v-for="(req, index) in chargeRequests"
                  :key="req.id"
                  class="charge-request-item"
                  :style="{ animationDelay: `${index * 0.05}s` }"
                >
                  <div
                    class="req-icon"
                    :class="getStatusClass(req.status_value)"
                  >
                    <i :class="getStatusIcon(req.status_value)"></i>
                  </div>
                  <div class="req-details">
                    <span class="req-bank">{{ req.bank_name }}</span>
                    <span class="req-amount"
                      >{{ req.amount }}
                      {{ $t("common.currency") || "د.ل" }}</span
                    >
                  </div>
                  <div class="req-meta">
                    <span
                      class="req-status"
                      :class="getStatusBadgeClass(req.status_value)"
                    >
                      {{ getStatusText(req.status_value) }}
                    </span>
                    <span class="req-date">{{
                      formatDate(req.created_at)
                    }}</span>
                  </div>
                </div>
              </div>

              <!-- Pagination -->
              <div
                v-if="
                  chargeRequests.length > 0 &&
                  chargeRequestsPagination.last_page > 1
                "
                class="pagination-wrapper"
              >
                <nav aria-label="صفحات الطلبات">
                  <ul class="pagination justify-content-center">
                    <li
                      class="page-item"
                      :class="{
                        disabled: !chargeRequestsPagination.prev_page_url,
                      }"
                    >
                      <a
                        class="page-link"
                        href="#"
                        @click.prevent="
                          getChargeRequests(
                            chargeRequestsPagination.current_page - 1
                          )
                        "
                      >
                        <i class="fas fa-chevron-right"></i>
                      </a>
                    </li>
                    <li class="page-item active">
                      <span class="page-link"
                        >{{ chargeRequestsPagination.current_page }} /
                        {{ chargeRequestsPagination.last_page }}</span
                      >
                    </li>
                    <li
                      class="page-item"
                      :class="{
                        disabled: !chargeRequestsPagination.next_page_url,
                      }"
                    >
                      <a
                        class="page-link"
                        href="#"
                        @click.prevent="
                          getChargeRequests(
                            chargeRequestsPagination.current_page + 1
                          )
                        "
                      >
                        <i class="fas fa-chevron-left"></i>
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Coupon Modal -->
  <Teleport to="body">
    <transition name="modal-fade">
      <div v-if="showCouponModal" class="coupon-modal-overlay" @click.self="closeCouponModal">
        <div class="coupon-modal">
          <!-- Modal Header -->
          <div class="coupon-modal__header">
            <div class="coupon-modal__icon">
              <i class="fas fa-ticket-alt"></i>
            </div>
            <button class="coupon-modal__close" @click="closeCouponModal">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <!-- Modal Body -->
          <div class="coupon-modal__body">
            <h2 class="coupon-modal__title">{{ $t("wallet.coupon_title") || "كوبون الاشتراك" }}</h2>
            <p class="coupon-modal__desc">{{ $t("wallet.coupon_desc") || "أدخل كود الخصم للحصول على عرض خاص" }}</p>

            <!-- Coupon Input -->
            <div class="coupon-modal__input-group">
              <div class="coupon-modal__input-wrapper">
                <i class="fas fa-tag"></i>
                <input
                  v-model="couponCode"
                  type="text"
                  class="coupon-modal__input"
                  :placeholder="$t('wallet.coupon_placeholder') || 'أدخل كود الكوبون هنا...'"
                  :disabled="couponLoading"
                  @keyup.enter="applyCoupon"
                  ref="couponInput"
                />
                <button
                  v-if="couponCode"
                  class="coupon-modal__clear"
                  @click="couponCode = ''"
                  :disabled="couponLoading"
                >
                  <i class="fas fa-times-circle"></i>
                </button>
              </div>
            </div>

            <!-- Result Message -->
            <transition name="fade">
              <div v-if="couponResult" class="coupon-modal__result" :class="couponResult.success ? 'success' : 'error'">
                <i :class="couponResult.success ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'"></i>
                <span>{{ couponResult.message }}</span>
              </div>
            </transition>
          </div>

          <!-- Modal Footer -->
          <div class="coupon-modal__footer">
            <button class="coupon-modal__cancel" @click="closeCouponModal">
              {{ $t("common.cancel") || "إلغاء" }}
            </button>
            <button
              class="coupon-modal__submit"
              :class="{ loading: couponLoading }"
              :disabled="!couponCode.trim() || couponLoading"
              @click="applyCoupon"
            >
              <span v-if="couponLoading" class="coupon-modal__spinner"></span>
              <i v-else class="fas fa-check"></i>
              <span>{{ couponLoading ? ($t("common.loading") || "جاري...") : ($t("wallet.apply_coupon") || "تطبيق الكوبون") }}</span>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>

  <!-- Send Money Modal -->
  <Teleport to="body">
    <transition name="modal-fade">
      <div v-if="showSendMoneyModal" class="send-money-modal-overlay" @click.self="closeSendMoneyModal">
        <div class="send-money-modal">
          <!-- Modal Header -->
          <div class="send-money-modal__header">
            <div class="send-money-modal__icon">
              <i class="fas fa-exchange-alt"></i>
            </div>
            <button class="send-money-modal__close" @click="closeSendMoneyModal">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <!-- Modal Body -->
          <div class="send-money-modal__body">
            <h2 class="send-money-modal__title">{{ $t("wallet.send_money_title") }}</h2>
            <p class="send-money-modal__desc">{{ $t("wallet.send_money_desc") }}</p>

            <!-- Phone Number Input -->
            <div class="send-money-modal__input-group">
              <label class="send-money-modal__label">{{ $t("wallet.recipient_number") }}</label>
              <div class="send-money-modal__phone-field">
                <input
                  ref="sendMoneyPhoneInput"
                  v-model="sendMoneyPhone"
                  type="tel"
                  class="send-money-modal__phone-input"
                  :placeholder="$t('wallet.phone_placeholder')"
                  :disabled="sendMoneyLoading"
                  @keyup.enter="sendMoney"
                />
              </div>
            </div>

            <!-- Amount Input -->
            <div class="send-money-modal__input-group">
              <label class="send-money-modal__label">{{ $t("wallet.amount") }}</label>
              <div class="send-money-modal__amount-wrapper">
                <i class="fas fa-coins"></i>
                <input
                  v-model="sendMoneyData.amount"
                  type="number"
                  min="1"
                  class="send-money-modal__amount-input"
                  :placeholder="$t('wallet.amount_placeholder')"
                  :disabled="sendMoneyLoading"
                  @keyup.enter="sendMoney"
                />
                <span class="send-money-modal__currency">{{ $t("wallet.currency") }}</span>
              </div>
            </div>

            <!-- Balance Info -->
            <div class="send-money-modal__balance-info">
              <span>{{ $t("wallet.available_balance") }}:</span>
              <strong>{{ walletBalanceDisplay }}</strong>
            </div>

            <!-- Result Message -->
            <transition name="fade">
              <div v-if="sendMoneyResult" class="send-money-modal__result" :class="sendMoneyResult.success ? 'success' : 'error'">
                <i :class="sendMoneyResult.success ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'"></i>
                <span>{{ sendMoneyResult.message }}</span>
              </div>
            </transition>
          </div>

          <!-- Modal Footer -->
          <div class="send-money-modal__footer">
            <button class="send-money-modal__cancel" @click="closeSendMoneyModal">
              {{ $t("common.cancel") }}
            </button>
            <button
              class="send-money-modal__submit"
              :class="{ loading: sendMoneyLoading }"
              :disabled="!canSendMoney || sendMoneyLoading"
              @click="sendMoney"
            >
              <span v-if="sendMoneyLoading" class="send-money-modal__spinner"></span>
              <i v-else class="fas fa-exchange-alt"></i>
              <span>{{ sendMoneyLoading ? $t("common.loading") : $t("wallet.send") }}</span>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>

  <!-- Payment Modals -->
  <Payments />
  <Academia />
  <Online />
  <LyPay />
  <YousrPay />
  <Icash />
  <MasrefyPay />
  <StripeAcademia />
</template>

<script>
import { apiUrl, api_get, api } from "@/utils/api";
import Payments from "./Payments.vue";
import Academia from "./Academia.vue";
import LyPay from "./LyPay.vue";
import YousrPay from "./yousrPay.vue";
import MasrefyPay from "./musrufypay.vue";
import Online from "./Online.vue";
import Icash from "./Icash.vue";
import StripeAcademia from "./Stripe.vue";
import { walletState, fetchBalance } from "@/stores/wallet";

export default {
  name: "Wallet",
  components: {
    Payments,
    Academia,
    Online,
    LyPay,
    YousrPay,
    Icash,
    MasrefyPay,
    StripeAcademia,
  },
  data() {
    return {
      token: localStorage.getItem("access_token"),
      isLoggedIn: false,
      isLoading: true,
      transactions: [],
      transactionsLoading: true,
      chargeRequests: [],
      chargeRequestsLoading: true,
      chargeRequestsPagination: {
        current_page: 1,
        last_page: 1,
        next_page_url: null,
        prev_page_url: null,
      },
      activeTab: "transactions",
      couponCode: "",
      couponLoading: false,
      couponResult: null,
      showCouponModal: false,
      // Send Money
      showSendMoneyModal: false,
      sendMoneyLoading: false,
      sendMoneyResult: null,
      sendMoneyData: {
        amount: "",
      },
      sendMoneyPhone: "",
      sendMoneyIti: null,
    };
  },
  watch: {
    showSendMoneyModal(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.initSendMoneyPhoneInput();
        });
      } else {
        if (this.sendMoneyIti) {
          this.sendMoneyIti.destroy();
          this.sendMoneyIti = null;
        }
      }
    },
  },
  mounted() {
    this.isLoggedIn =
      walletState.isLoggedIn || !!localStorage.getItem("access_token");
    this.isLoading = false;
    if (this.isLoggedIn) {
      fetchBalance({ force: true });
      this.getTransactions();
      this.getChargeRequests();
    }
  },
  computed: {
    loadingBalance() {
      return walletState.loading;
    },
    walletBalanceDisplay() {
      const val = walletState.walletBalance;
      if (val == null || typeof val === "undefined") {
        return window.appCurrency ? "0 " + window.appCurrency : "0";
      }
      const num = Number(val);
      if (!Number.isNaN(num)) {
        const fixed = num.toFixed(2);
        return window.appCurrency
          ? fixed + " " + window.appCurrency
          : String(fixed);
      }
      return window.appCurrency
        ? String(val) + " " + window.appCurrency
        : String(val);
    },
    canSendMoney() {
      return (
        this.sendMoneyPhone.trim().length > 0 &&
        this.sendMoneyData.amount !== "" &&
        Number(this.sendMoneyData.amount) > 0
      );
    },
  },
  methods: {
    async getTransactions() {
      this.transactionsLoading = true;
      try {
        const response = await api_get(
          "/wallets/get-transactions?secret=a463c0cd-78ed-4bda-a5f6-f70c3e597dfb",
          { Authorization: `Bearer ${this.token}` }
        );
        if (response.ok) {
          const data = await response.json();
          this.transactions = data.transactions || [];
        }
      } catch (error) {
        console.error("Error retrieving transactions:", error);
      } finally {
        this.transactionsLoading = false;
      }
    },
    async getChargeRequests(page = 1) {
      this.chargeRequestsLoading = true;
      try {
        const response = await api_get(`/lypay/my-requests?page=${page}`, {
          Authorization: `Bearer ${this.token}`,
        });
        if (response.ok) {
          const data = await response.json();
          this.chargeRequests = data.data || [];
          this.chargeRequestsPagination = {
            current_page: data.meta?.current_page || 1,
            last_page: data.meta?.last_page || 1,
            next_page_url: data.links?.next || null,
            prev_page_url: data.links?.prev || null,
          };
        }
      } catch (error) {
        console.error("Error retrieving charge requests:", error);
      } finally {
        this.chargeRequestsLoading = false;
      }
    },
    getStatusClass(status) {
      switch (status) {
        case "approved":
          return "status-approved";
        case "rejected":
          return "status-rejected";
        case "pending":
        default:
          return "status-pending";
      }
    },
    getStatusIcon(status) {
      switch (status) {
        case "approved":
          return "fas fa-check-circle";
        case "rejected":
          return "fas fa-times-circle";
        case "pending":
        default:
          return "fas fa-clock";
      }
    },
    getStatusBadgeClass(status) {
      switch (status) {
        case "approved":
          return "badge-approved";
        case "rejected":
          return "badge-rejected";
        case "pending":
        default:
          return "badge-pending";
      }
    },
    getStatusText(status) {
      switch (status) {
        case "approved":
          return this.$t("wallet.status_approved") || "مقبول";
        case "rejected":
          return this.$t("wallet.status_rejected") || "مرفوض";
        case "pending":
        default:
          return this.$t("wallet.status_pending") || "قيد المراجعة";
      }
    },
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("ar-LY", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    logout() {
      api(
        "/logout",
        "POST",
        {},
        { Authorization: `Bearer ${this.token}` },
        false
      ).then(async () => {
        localStorage.removeItem("access_token");
        window.location.href = "/login";
      });
      localStorage.removeItem("access_token");
    },
    showModal(modal) {
      var myModal = new bootstrap.Modal(document.getElementById(modal));
      myModal.show();
    },
    closeCouponModal() {
      this.showCouponModal = false;
      this.couponCode = "";
      this.couponResult = null;
    },
    async applyCoupon() {
      if (!this.couponCode.trim() || this.couponLoading) return;

      this.couponLoading = true;
      this.couponResult = null;

      try {
        const response = await fetch(`${apiUrl}/checkout/coupon`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify({
            coupon_code: this.couponCode.trim(),
          }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          this.couponResult = {
            success: true,
            message: data.message || this.$t("wallet.coupon_applied") || "تم تطبيق الكوبون بنجاح!",
          };
          this.couponCode = "";
          // Refresh balance after successful coupon application
          fetchBalance({ force: true });
          // Close modal after 2 seconds on success
          setTimeout(() => {
            this.closeCouponModal();
          }, 2000);
        } else {
          this.couponResult = {
            success: false,
            message: data.message || this.$t("wallet.coupon_invalid") || "كود الكوبون غير صالح",
          };
        }
      } catch (error) {
        console.error("Error applying coupon:", error);
        this.couponResult = {
          success: false,
          message: this.$t("wallet.coupon_error") || "حدث خطأ أثناء تطبيق الكوبون",
        };
      } finally {
        this.couponLoading = false;
      }
    },
    initSendMoneyPhoneInput() {
      const phoneInput = this.$refs.sendMoneyPhoneInput;
      if (phoneInput && typeof intlTelInput !== 'undefined') {
        this.sendMoneyIti = intlTelInput(phoneInput, {
          utilsScript: "assets/js/utils.js",
          initialCountry: "ly",
          separateDialCode: true,
          excludeCountries: ["il"],
          nationalMode: true,
          formatOnDisplay: false,
          preferredCountries: ["ly", "eg", "sa", "ae", "jo", "ma", "tn", "sd", "kw", "qa", "bh", "om"],
        });
      }
    },
    closeSendMoneyModal() {
      this.showSendMoneyModal = false;
      this.sendMoneyData = {
        amount: "",
      };
      this.sendMoneyPhone = "";
      this.sendMoneyResult = null;
    },
    async sendMoney() {
      if (!this.canSendMoney || this.sendMoneyLoading) return;

      this.sendMoneyLoading = true;
      this.sendMoneyResult = null;

      try {
        // Get phone number from intlTelInput
        let fullPhoneNumber = "";
        let countryCode = "+218";
        
        if (this.sendMoneyIti) {
          fullPhoneNumber = this.sendMoneyIti.getNumber();
          const countryData = this.sendMoneyIti.getSelectedCountryData();
          countryCode = "+" + countryData.dialCode;
        } else if (this.$refs.sendMoneyPhoneInput) {
          fullPhoneNumber = this.$refs.sendMoneyPhoneInput.value;
        }
        
        const response = await fetch(`${apiUrl}/wallets/transfer`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify({
            amount: this.sendMoneyData.amount,
            to_mobile: fullPhoneNumber,
            country_code: countryCode,
          }),
        });

        const data = await response.json();

        if (response.ok) {
          this.sendMoneyResult = {
            success: true,
            message: data.message || this.$t("wallet.send_money_success"),
          };
          // Refresh balance after successful transfer
          fetchBalance({ force: true });
          this.getTransactions();
          // Close modal after 2 seconds on success
          setTimeout(() => {
            this.closeSendMoneyModal();
          }, 2000);
        } else {
          // Handle validation errors
          let errorMessage = data.message || this.$t("wallet.send_money_error");
          if (data.errors) {
            const errors = Object.values(data.errors).flat();
            errorMessage = errors.join("\n");
          }
          this.sendMoneyResult = {
            success: false,
            message: errorMessage,
          };
        }
      } catch (error) {
        console.error("Error sending money:", error);
        this.sendMoneyResult = {
          success: false,
          message: this.$t("wallet.send_money_error"),
        };
      } finally {
        this.sendMoneyLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ===== Main Page Styles ===== */
.wallet-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fffe 0%, #e8fff1 100%);
  direction: rtl;
}

/* ===== Hero Section ===== */
.hero-section {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 50%, #008f32 100%);
  padding: 60px 20px;
  position: relative;
  overflow: hidden;
  border-radius: 0 0 50px 50px;
  margin-bottom: 40px;
  box-shadow: 0 10px 40px rgba(0, 199, 68, 0.3);
}

.hero-content {
  text-align: center;
  position: relative;
  z-index: 2;
}

.hero-icon {
  width: 100px;
  height: 100px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  backdrop-filter: blur(10px);
  animation: float 3s ease-in-out infinite;
}

.hero-icon i {
  font-size: 45px;
  color: white;
}

.hero-title {
  color: white;
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 10px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.hero-subtitle {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
  font-weight: 400;
}

.hero-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
}

.circle-1 {
  width: 200px;
  height: 200px;
  top: -50px;
  right: -50px;
  animation: pulse 4s ease-in-out infinite;
}

.circle-2 {
  width: 150px;
  height: 150px;
  bottom: -30px;
  left: 10%;
  animation: pulse 5s ease-in-out infinite 1s;
}

.circle-3 {
  width: 100px;
  height: 100px;
  top: 30%;
  left: 5%;
  animation: pulse 6s ease-in-out infinite 2s;
}

/* ===== Wallet Container ===== */
.wallet-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 20px 60px;
}

/* ===== Loading State ===== */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 20px;
}

.loading-spinner {
  position: relative;
  width: 80px;
  height: 80px;
}

.loading-spinner.small {
  width: 50px;
  height: 50px;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 4px solid transparent;
  border-top-color: #00c744;
  border-radius: 50%;
  animation: spin 1.2s linear infinite;
}

.spinner-ring:nth-child(2) {
  width: 70%;
  height: 70%;
  top: 15%;
  left: 15%;
  border-top-color: #00a83b;
  animation-duration: 1s;
}

.spinner-ring:nth-child(3) {
  width: 40%;
  height: 40%;
  top: 30%;
  left: 30%;
  border-top-color: #008f32;
  animation-duration: 0.8s;
}

.loading-text {
  margin-top: 20px;
  color: #00c744;
  font-size: 1.1rem;
  font-weight: 600;
}

/* ===== Login Prompt ===== */
.login-prompt {
  display: flex;
  justify-content: center;
  padding: 60px 20px;
}

.login-card {
  background: white;
  border-radius: 30px;
  padding: 60px 50px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
  max-width: 450px;
  width: 100%;
}

.login-icon {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 30px;
  box-shadow: 0 10px 30px rgba(0, 199, 68, 0.3);
}

.login-icon i {
  font-size: 40px;
  color: white;
}

.login-card h2 {
  color: #2d3748;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 15px;
}

.login-desc {
  color: #718096;
  font-size: 1rem;
  margin-bottom: 30px;
}

.login-btn {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  padding: 16px 40px;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(0, 199, 68, 0.3);
}

.login-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 40px rgba(0, 199, 68, 0.4);
  color: white;
}

/* ===== Balance Card ===== */
.balance-card {
  position: relative;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 50%, #008f32 100%);
  border-radius: 30px;
  padding: 40px;
  margin-bottom: 30px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 199, 68, 0.3);
}

.balance-card-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
}

.balance-pattern {
  position: absolute;
  width: 300px;
  height: 300px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 50%;
  top: -100px;
  right: -100px;
}

.balance-card-content {
  position: relative;
  z-index: 2;
}

.balance-header {
  display: flex;
  align-items: center;
  gap: 25px;
  margin-bottom: 30px;
}

.balance-icon-wrapper {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.wallet-img {
  width: 50px;
  height: 50px;
  object-fit: contain;
}

.balance-info {
  flex: 1;
}

.balance-label {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
  display: block;
  margin-bottom: 8px;
}

.balance-amount {
  display: flex;
  align-items: center;
}

.amount-value {
  color: white;
  font-size: 2.5rem;
  font-weight: 800;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.balance-loading {
  display: flex;
  align-items: center;
}

.mini-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.balance-actions {
  display: flex;
  justify-content: flex-end;
  gap: 14px;
}

.recharge-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: white;
  color: #00c744;
  padding: 14px 35px;
  border: none;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.recharge-btn:hover {
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
}

.recharge-btn i {
  font-size: 1.2rem;
}

.coupon-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 14px 28px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50px;
  margin-right: 6px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.coupon-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.coupon-btn i {
  font-size: 1.2rem;
  color: #ffffff !important;
}

.send-money-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 14px 28px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.send-money-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.send-money-btn i {
  font-size: 1.2rem;
  color: #ffffff !important;
}

.card-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.1),
    transparent
  );
  animation: shine 3s infinite;
}

/* ===== Coupon Modal ===== */
.coupon-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.coupon-modal {
  background: white;
  border-radius: 28px;
  width: 100%;
  max-width: 450px;
  overflow: hidden;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.25);
  animation: modalSlideIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.coupon-modal__header {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  padding: 30px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.coupon-modal__header::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.08'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  pointer-events: none;
}

.coupon-modal__icon {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  position: relative;
  z-index: 1;
}

.coupon-modal__icon i {
  font-size: 36px;
  color: white;
}

.coupon-modal__close {
  position: absolute;
  top: 15px;
  left: 15px;
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  color: #ffffff !important;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.coupon-modal__close i {
  color: #ffffff !important;
}

.coupon-modal__close:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

.coupon-modal__body {
  padding: 30px;
  text-align: center;
}

.coupon-modal__title {
  margin: 0 0 10px 0;
  font-size: 1.5rem;
  font-weight: 800;
  color: #1a1a2e;
}

.coupon-modal__desc {
  margin: 0 0 25px 0;
  font-size: 0.95rem;
  color: #718096;
  line-height: 1.6;
}

.coupon-modal__input-group {
  margin-bottom: 20px;
}

.coupon-modal__input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.coupon-modal__input-wrapper > i {
  position: absolute;
  right: 18px;
  color: #00c744;
  font-size: 1.1rem;
  pointer-events: none;
}

.coupon-modal__input {
  width: 100%;
  padding: 18px 50px 18px 50px;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a1a2e;
  background: #f8fafc;
  transition: all 0.3s ease;
  text-align: center;
  letter-spacing: 2px;
}

.coupon-modal__input:focus {
  outline: none;
  border-color: #00c744;
  background: white;
  box-shadow: 0 0 0 4px rgba(0, 199, 68, 0.15);
}

.coupon-modal__input::placeholder {
  color: #a0aec0;
  letter-spacing: normal;
  font-weight: 500;
}

.coupon-modal__input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.coupon-modal__clear {
  position: absolute;
  left: 14px;
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  color: #a0aec0;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.coupon-modal__clear:hover {
  color: #e53e3e;
}

.coupon-modal__result {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 20px;
  border-radius: 14px;
  font-size: 0.95rem;
  font-weight: 600;
  animation: fadeInUp 0.3s ease;
}

.coupon-modal__result.success {
  background: linear-gradient(135deg, #d4f7e4 0%, #b8f0d0 100%);
  color: #0d6832;
  border: 1px solid rgba(0, 199, 68, 0.2);
}

.coupon-modal__result.error {
  background: linear-gradient(135deg, #ffe4e4 0%, #ffd0d0 100%);
  color: #9b1c1c;
  border: 1px solid rgba(220, 38, 38, 0.2);
}

.coupon-modal__result i {
  font-size: 1.2rem;
  color: inherit !important;
}

.coupon-modal__footer {
  padding: 0 30px 30px;
  display: flex;
  gap: 12px;
}

.coupon-modal__cancel {
  flex: 1;
  padding: 16px 24px;
  border: 2px solid #e2e8f0;
  background: white;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 600;
  color: #718096;
  cursor: pointer;
  transition: all 0.3s ease;
}

.coupon-modal__cancel:hover {
  background: #f7fafc;
  border-color: #cbd5e0;
  color: #4a5568;
}

.coupon-modal__submit {
  flex: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 24px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(0, 199, 68, 0.3);
}

.coupon-modal__submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 12px 35px rgba(0, 199, 68, 0.4);
}

.coupon-modal__submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.coupon-modal__submit.loading {
  pointer-events: none;
}

.coupon-modal__spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* Modal Transitions */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .coupon-modal,
.modal-fade-leave-to .coupon-modal {
  transform: scale(0.9) translateY(20px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ===== Send Money Modal ===== */
.send-money-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.send-money-modal {
  background: white;
  border-radius: 28px;
  width: 100%;
  max-width: 480px;
  overflow: hidden;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.25);
  animation: modalSlideIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.send-money-modal__header {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  padding: 30px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.send-money-modal__header::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.08'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  pointer-events: none;
}

.send-money-modal__icon {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  position: relative;
  z-index: 1;
}

.send-money-modal__icon i {
  font-size: 36px;
  color: white;
}

.send-money-modal__close {
  position: absolute;
  top: 15px;
  left: 15px;
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  color: #ffffff !important;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.send-money-modal__close i {
  color: #ffffff !important;
}

.send-money-modal__close:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

.send-money-modal__body {
  padding: 30px;
}

.send-money-modal__title {
  margin: 0 0 10px 0;
  font-size: 1.5rem;
  font-weight: 800;
  color: #1a1a2e;
  text-align: center;
}

.send-money-modal__desc {
  margin: 0 0 25px 0;
  font-size: 0.95rem;
  color: #718096;
  line-height: 1.6;
  text-align: center;
}

.send-money-modal__input-group {
  margin-bottom: 20px;
}

.send-money-modal__label {
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 8px;
}

.send-money-modal__phone-field {
  width: 100%;
}

.send-money-modal__phone-field .iti {
  width: 100%;
}

.send-money-modal__phone-field .iti__flag-container {
  direction: ltr;
}

.send-money-modal__phone-field .iti__selected-flag {
  padding: 0 12px;
  background: #f8fafc;
  border-radius: 12px 0 0 12px;
  border: 2px solid #e2e8f0;
  border-right: none;
}

.send-money-modal__phone-field .iti__selected-flag:hover {
  background: #f1f5f9;
}

.send-money-modal__phone-field .iti--separate-dial-code .iti__selected-flag {
  background: #f8fafc;
}

.send-money-modal__phone-input {
  width: 100%;
  padding: 14px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 0 12px 12px 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a2e;
  background: #f8fafc;
  transition: all 0.3s ease;
  text-align: left;
  direction: ltr;
}

.send-money-modal__phone-field .iti--separate-dial-code input {
  padding-left: 100px !important;
}

.send-money-modal__phone-input:focus {
  outline: none;
  border-color: #00c744;
  background: white;
  box-shadow: 0 0 0 4px rgba(0, 199, 68, 0.15);
}

.send-money-modal__phone-input::placeholder {
  color: #a0aec0;
  font-weight: 500;
}

.send-money-modal__amount-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.send-money-modal__amount-wrapper > i {
  position: absolute;
  right: 16px;
  color: #00c744;
  font-size: 1.1rem;
  pointer-events: none;
}

.send-money-modal__amount-input {
  width: 100%;
  padding: 16px 50px 16px 80px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1a1a2e;
  background: #f8fafc;
  transition: all 0.3s ease;
  text-align: center;
}

.send-money-modal__amount-input:focus {
  outline: none;
  border-color: #00c744;
  background: white;
  box-shadow: 0 0 0 4px rgba(0, 199, 68, 0.15);
}

.send-money-modal__amount-input::placeholder {
  color: #a0aec0;
  font-weight: 500;
  font-size: 1rem;
}

.send-money-modal__currency {
  position: absolute;
  left: 16px;
  color: #64748b;
  font-size: 0.9rem;
  font-weight: 600;
}

.send-money-modal__balance-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: #e8fff1;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #00c744;
  margin-bottom: 20px;
}

.send-money-modal__balance-info strong {
  color: #00a83b;
}

.send-money-modal__result {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 20px;
  border-radius: 14px;
  font-size: 0.95rem;
  font-weight: 600;
  animation: fadeInUp 0.3s ease;
}

.send-money-modal__result.success {
  background: linear-gradient(135deg, #d4f7e4 0%, #b8f0d0 100%);
  color: #0d6832;
  border: 1px solid rgba(0, 199, 68, 0.2);
}

.send-money-modal__result.error {
  background: linear-gradient(135deg, #ffe4e4 0%, #ffd0d0 100%);
  color: #9b1c1c;
  border: 1px solid rgba(220, 38, 38, 0.2);
}

.send-money-modal__result i {
  font-size: 1.2rem;
  color: inherit !important;
}

.send-money-modal__footer {
  padding: 0 30px 30px;
  display: flex;
  gap: 12px;
}

.send-money-modal__cancel {
  flex: 1;
  padding: 16px 24px;
  border: 2px solid #e2e8f0;
  background: white;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 600;
  color: #718096;
  cursor: pointer;
  transition: all 0.3s ease;
}

.send-money-modal__cancel:hover {
  background: #f7fafc;
  border-color: #cbd5e0;
  color: #4a5568;
}

.send-money-modal__submit {
  flex: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 24px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(0, 199, 68, 0.3);
}

.send-money-modal__submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 12px 35px rgba(0, 199, 68, 0.4);
}

.send-money-modal__submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.send-money-modal__submit.loading {
  pointer-events: none;
}

.send-money-modal__spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* ===== Wallet Tabs Section ===== */
.wallet-tabs-section {
  background: white;
  border-radius: 30px;
  padding: 30px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
}

.tabs-header {
  display: flex;
  gap: 10px;
  margin-bottom: 25px;
  border-bottom: 2px solid #edf2f7;
  padding-bottom: 15px;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  border: none;
  background: transparent;
  color: #718096;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.tab-btn:hover {
  background: #e8fff1;
  color: #00c744;
}

.tab-btn.active {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  box-shadow: 0 5px 20px rgba(0, 199, 68, 0.3);
}

.tab-btn i {
  font-size: 1.1rem;
}

.tabs-content {
  min-height: 300px;
}

.tab-pane {
  max-height: 450px;
  overflow-y: auto;
}

.transactions-loading {
  display: flex;
  justify-content: center;
  padding: 50px 0;
}

.transactions-empty {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}

.empty-icon i {
  font-size: 35px;
  color: #00c744;
}

.transactions-empty p {
  color: #718096;
  font-size: 1rem;
}

/* ===== Transaction Item ===== */
.transactions-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
  overflow-y: auto;
  max-height: 380px;
  padding-left: 5px;
  scrollbar-width: thin;
  scrollbar-color: #00c744 #e8fff1;
}

.transactions-list::-webkit-scrollbar {
  width: 6px;
}

.transactions-list::-webkit-scrollbar-track {
  background: #e8fff1;
  border-radius: 10px;
}

.transactions-list::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border-radius: 10px;
}

.transactions-list::-webkit-scrollbar-thumb:hover {
  background: #008f32;
}

.transaction-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  background: #f8fffe;
  border-radius: 20px;
  transition: all 0.3s ease;
  animation: fadeInUp 0.5s ease forwards;
  opacity: 0;
}

.transaction-item:hover {
  transform: translateX(-5px);
  box-shadow: 0 10px 30px rgba(0, 199, 68, 0.1);
}

.tx-icon {
  width: 50px;
  height: 50px;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.transaction-item.credit .tx-icon {
  background: linear-gradient(135deg, #d4f7e4 0%, #b8f0d0 100%);
}

.transaction-item.credit .tx-icon i {
  color: #00c744;
  font-size: 1.2rem;
}

.transaction-item.debit .tx-icon {
  background: linear-gradient(135deg, #ffe4e4 0%, #ffd0d0 100%);
}

.transaction-item.debit .tx-icon i {
  color: #ff4141;
  font-size: 1.2rem;
}

.tx-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.tx-type {
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
}

.tx-description {
  font-size: 0.9rem;
  color: #718096;
}

.tx-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
}

.tx-date {
  font-size: 0.85rem;
  color: #718096;
}

.tx-index {
  font-size: 0.8rem;
  color: #a0aec0;
  background: #edf2f7;
  padding: 3px 10px;
  border-radius: 10px;
}

/* ===== Charge Requests Section ===== */
.charge-requests-section {
  background: white;
  border-radius: 30px;
  padding: 30px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
  margin-bottom: 30px;
}

.charge-requests-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
  max-height: 350px;
  overflow-y: auto;
  padding-left: 5px;
  scrollbar-width: thin;
  scrollbar-color: #00c744 #e8fff1;
}

.charge-requests-list::-webkit-scrollbar {
  width: 6px;
}

.charge-requests-list::-webkit-scrollbar-track {
  background: #e8fff1;
  border-radius: 10px;
}

.charge-requests-list::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border-radius: 10px;
}

.charge-request-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 18px;
  background: #f8fffe;
  border-radius: 20px;
  transition: all 0.3s ease;
  animation: fadeInUp 0.5s ease forwards;
  opacity: 0;
}

.charge-request-item:hover {
  transform: translateX(-5px);
  box-shadow: 0 10px 30px rgba(0, 199, 68, 0.1);
}

.req-icon {
  width: 50px;
  height: 50px;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.req-icon.status-pending {
  background: linear-gradient(135deg, #fff3cd 0%, #ffe69c 100%);
}

.req-icon.status-pending i {
  color: #856404;
  font-size: 1.2rem;
}

.req-icon.status-approved {
  background: linear-gradient(135deg, #d4f7e4 0%, #b8f0d0 100%);
}

.req-icon.status-approved i {
  color: #00c744;
  font-size: 1.2rem;
}

.req-icon.status-rejected {
  background: linear-gradient(135deg, #ffe4e4 0%, #ffd0d0 100%);
}

.req-icon.status-rejected i {
  color: #ff4141;
  font-size: 1.2rem;
}

.req-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.req-bank {
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
}

.req-amount {
  font-size: 0.95rem;
  color: #00c744;
  font-weight: 600;
}

.req-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.req-status {
  font-size: 0.8rem;
  font-weight: 600;
  padding: 5px 12px;
  border-radius: 20px;
}

.req-status.badge-pending {
  background: #fff3cd;
  color: #856404;
}

.req-status.badge-approved {
  background: #d4edda;
  color: #155724;
}

.req-status.badge-rejected {
  background: #f8d7da;
  color: #721c24;
}

.req-date {
  font-size: 0.8rem;
  color: #718096;
}

.pagination-wrapper {
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #edf2f7;
}

.pagination-wrapper .pagination {
  margin: 0;
}

.pagination-wrapper .page-link {
  border: none;
  color: #00c744;
  background: transparent;
  padding: 8px 15px;
  border-radius: 10px;
  transition: all 0.3s ease;
}

.pagination-wrapper .page-item.active .page-link {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
}

.pagination-wrapper .page-item.disabled .page-link {
  color: #a0aec0;
  cursor: not-allowed;
}

.pagination-wrapper .page-link:hover:not(.disabled) {
  background: #e8fff1;
}

/* ===== Animations ===== */
@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.2;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes shine {
  0% {
    left: -100%;
  }
  50%,
  100% {
    left: 100%;
  }
}

/* ===== Dark Mode Support ===== */
[data-bs-theme="dark"] .wallet-page,
[data-theme="dark"] .wallet-page,
.dark .wallet-page {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
}

[data-bs-theme="dark"] .coupon-modal,
[data-theme="dark"] .coupon-modal,
.dark .coupon-modal {
  background: #1e293b;
}

[data-bs-theme="dark"] .coupon-modal__title,
[data-theme="dark"] .coupon-modal__title,
.dark .coupon-modal__title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .coupon-modal__desc,
[data-theme="dark"] .coupon-modal__desc,
.dark .coupon-modal__desc {
  color: #94a3b8;
}

[data-bs-theme="dark"] .coupon-modal__input,
[data-theme="dark"] .coupon-modal__input,
.dark .coupon-modal__input {
  background: #0f172a;
  border-color: #334155;
  color: #f1f5f9;
}

[data-bs-theme="dark"] .coupon-modal__input:focus,
[data-theme="dark"] .coupon-modal__input:focus,
.dark .coupon-modal__input:focus {
  background: #1e293b;
  border-color: #4ade80;
  box-shadow: 0 0 0 4px rgba(74, 222, 128, 0.1);
}

[data-bs-theme="dark"] .coupon-modal__input::placeholder,
[data-theme="dark"] .coupon-modal__input::placeholder,
.dark .coupon-modal__input::placeholder {
  color: #64748b;
}

[data-bs-theme="dark"] .coupon-modal__cancel,
[data-theme="dark"] .coupon-modal__cancel,
.dark .coupon-modal__cancel {
  background: #0f172a;
  border-color: #334155;
  color: #94a3b8;
}

[data-bs-theme="dark"] .coupon-modal__cancel:hover,
[data-theme="dark"] .coupon-modal__cancel:hover,
.dark .coupon-modal__cancel:hover {
  background: #1e293b;
  border-color: #475569;
  color: #f1f5f9;
}

[data-bs-theme="dark"] .coupon-modal__result.success,
[data-theme="dark"] .coupon-modal__result.success,
.dark .coupon-modal__result.success {
  background: linear-gradient(135deg, #0f3d1c 0%, #1a5c2e 100%);
  color: #4ade80;
  border-color: rgba(74, 222, 128, 0.3);
}

[data-bs-theme="dark"] .coupon-modal__result.error,
[data-theme="dark"] .coupon-modal__result.error,
.dark .coupon-modal__result.error {
  background: linear-gradient(135deg, #3d1c1c 0%, #5c2e2e 100%);
  color: #fca5a5;
  border-color: rgba(252, 165, 165, 0.3);
}

/* Send Money Modal Dark Mode */
[data-bs-theme="dark"] .send-money-modal,
[data-theme="dark"] .send-money-modal,
.dark .send-money-modal {
  background: #1e293b;
}

[data-bs-theme="dark"] .send-money-modal__title,
[data-theme="dark"] .send-money-modal__title,
.dark .send-money-modal__title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .send-money-modal__desc,
[data-theme="dark"] .send-money-modal__desc,
.dark .send-money-modal__desc {
  color: #94a3b8;
}

[data-bs-theme="dark"] .send-money-modal__label,
[data-theme="dark"] .send-money-modal__label,
.dark .send-money-modal__label {
  color: #cbd5e1;
}

[data-bs-theme="dark"] .send-money-modal__phone-input,
[data-bs-theme="dark"] .send-money-modal__amount-input,
[data-theme="dark"] .send-money-modal__phone-input,
[data-theme="dark"] .send-money-modal__amount-input,
.dark .send-money-modal__phone-input,
.dark .send-money-modal__amount-input {
  background: #0f172a;
  border-color: #334155;
  color: #f1f5f9;
}

[data-bs-theme="dark"] .send-money-modal__phone-field .iti__selected-flag,
[data-theme="dark"] .send-money-modal__phone-field .iti__selected-flag,
.dark .send-money-modal__phone-field .iti__selected-flag {
  background: #0f172a;
  border-color: #334155;
}

[data-bs-theme="dark"] .send-money-modal__phone-input:focus,
[data-bs-theme="dark"] .send-money-modal__amount-input:focus,
[data-theme="dark"] .send-money-modal__phone-input:focus,
[data-theme="dark"] .send-money-modal__amount-input:focus,
.dark .send-money-modal__phone-input:focus,
.dark .send-money-modal__amount-input:focus {
  background: #1e293b;
  border-color: #4ade80;
  box-shadow: 0 0 0 4px rgba(74, 222, 128, 0.15);
}

[data-bs-theme="dark"] .send-money-modal__balance-info,
[data-theme="dark"] .send-money-modal__balance-info,
.dark .send-money-modal__balance-info {
  background: #1a3d2e;
  color: #4ade80;
}

[data-bs-theme="dark"] .send-money-modal__balance-info strong,
[data-theme="dark"] .send-money-modal__balance-info strong,
.dark .send-money-modal__balance-info strong {
  color: #86efac;
}

[data-bs-theme="dark"] .send-money-modal__cancel,
[data-theme="dark"] .send-money-modal__cancel,
.dark .send-money-modal__cancel {
  background: #0f172a;
  border-color: #334155;
  color: #94a3b8;
}

[data-bs-theme="dark"] .send-money-modal__cancel:hover,
[data-theme="dark"] .send-money-modal__cancel:hover,
.dark .send-money-modal__cancel:hover {
  background: #1e293b;
  border-color: #475569;
  color: #f1f5f9;
}

[data-bs-theme="dark"] .send-money-modal__result.success,
[data-theme="dark"] .send-money-modal__result.success,
.dark .send-money-modal__result.success {
  background: linear-gradient(135deg, #0f3d1c 0%, #1a5c2e 100%);
  color: #4ade80;
  border-color: rgba(74, 222, 128, 0.3);
}

[data-bs-theme="dark"] .send-money-modal__result.error,
[data-theme="dark"] .send-money-modal__result.error,
.dark .send-money-modal__result.error {
  background: linear-gradient(135deg, #3d1c1c 0%, #5c2e2e 100%);
  color: #fca5a5;
  border-color: rgba(252, 165, 165, 0.3);
}

[data-bs-theme="dark"] .login-card,
[data-theme="dark"] .login-card,
.dark .login-card {
  background: #1e293b;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

[data-bs-theme="dark"] .login-card h2,
[data-theme="dark"] .login-card h2,
.dark .login-card h2 {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .login-desc,
[data-theme="dark"] .login-desc,
.dark .login-desc {
  color: #94a3b8;
}

[data-bs-theme="dark"] .transactions-section,
[data-theme="dark"] .transactions-section,
.dark .transactions-section {
  background: #1e293b;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

[data-bs-theme="dark"] .section-title h3,
[data-theme="dark"] .section-title h3,
.dark .section-title h3 {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .transaction-item,
[data-theme="dark"] .transaction-item,
.dark .transaction-item {
  background: #0f172a;
}

[data-bs-theme="dark"] .tx-type,
[data-theme="dark"] .tx-type,
.dark .tx-type {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .tx-description,
[data-theme="dark"] .tx-description,
.dark .tx-description {
  color: #94a3b8;
}

[data-bs-theme="dark"] .tx-date,
[data-theme="dark"] .tx-date,
.dark .tx-date {
  color: #94a3b8;
}

[data-bs-theme="dark"] .tx-index,
[data-theme="dark"] .tx-index,
.dark .tx-index {
  background: #334155;
  color: #94a3b8;
}

[data-bs-theme="dark"] .empty-icon,
[data-theme="dark"] .empty-icon,
.dark .empty-icon {
  background: linear-gradient(135deg, #0f3d1c 0%, #1a5c2e 100%);
}

[data-bs-theme="dark"] .transactions-empty p,
[data-theme="dark"] .transactions-empty p,
.dark .transactions-empty p {
  color: #94a3b8;
}

[data-bs-theme="dark"] .loading-text,
[data-theme="dark"] .loading-text,
.dark .loading-text {
  color: #4ade80;
}

[data-bs-theme="dark"] .transaction-item.credit .tx-icon,
[data-theme="dark"] .transaction-item.credit .tx-icon,
.dark .transaction-item.credit .tx-icon {
  background: linear-gradient(135deg, #0f3d1c 0%, #1a5c2e 100%);
}

[data-bs-theme="dark"] .transaction-item.debit .tx-icon,
[data-theme="dark"] .transaction-item.debit .tx-icon,
.dark .transaction-item.debit .tx-icon {
  background: linear-gradient(135deg, #3d1c1c 0%, #5c2e2e 100%);
}

[data-bs-theme="dark"] .transactions-list,
[data-theme="dark"] .transactions-list,
.dark .transactions-list {
  scrollbar-color: #4ade80 #1e293b;
}

[data-bs-theme="dark"] .transactions-list::-webkit-scrollbar-track,
[data-theme="dark"] .transactions-list::-webkit-scrollbar-track,
.dark .transactions-list::-webkit-scrollbar-track {
  background: #1e293b;
}

[data-bs-theme="dark"] .transactions-list::-webkit-scrollbar-thumb,
[data-theme="dark"] .transactions-list::-webkit-scrollbar-thumb,
.dark .transactions-list::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
}

/* ===== Tabs Dark Mode ===== */
[data-bs-theme="dark"] .wallet-tabs-section,
[data-theme="dark"] .wallet-tabs-section,
.dark .wallet-tabs-section {
  background: #1e293b;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

[data-bs-theme="dark"] .tabs-header,
[data-theme="dark"] .tabs-header,
.dark .tabs-header {
  border-bottom-color: #334155;
}

[data-bs-theme="dark"] .tab-btn,
[data-theme="dark"] .tab-btn,
.dark .tab-btn {
  color: #94a3b8;
}

[data-bs-theme="dark"] .tab-btn:hover,
[data-theme="dark"] .tab-btn:hover,
.dark .tab-btn:hover {
  background: #0f3d1c;
  color: #4ade80;
}

/* ===== Charge Requests Dark Mode ===== */

[data-bs-theme="dark"] .charge-request-item,
[data-theme="dark"] .charge-request-item,
.dark .charge-request-item {
  background: #0f172a;
}

[data-bs-theme="dark"] .req-bank,
[data-theme="dark"] .req-bank,
.dark .req-bank {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .req-date,
[data-theme="dark"] .req-date,
.dark .req-date {
  color: #94a3b8;
}

[data-bs-theme="dark"] .pagination-wrapper,
[data-theme="dark"] .pagination-wrapper,
.dark .pagination-wrapper {
  border-top-color: #334155;
}

[data-bs-theme="dark"] .charge-requests-list,
[data-theme="dark"] .charge-requests-list,
.dark .charge-requests-list {
  scrollbar-color: #4ade80 #1e293b;
}

[data-bs-theme="dark"] .charge-requests-list::-webkit-scrollbar-track,
[data-theme="dark"] .charge-requests-list::-webkit-scrollbar-track,
.dark .charge-requests-list::-webkit-scrollbar-track {
  background: #1e293b;
}

/* ===== Responsive ===== */
@media (max-width: 768px) {
  .hero-section {
    padding: 40px 20px;
    border-radius: 0 0 30px 30px;
  }

  .hero-title {
    font-size: 1.8rem;
  }

  .hero-icon {
    width: 80px;
    height: 80px;
  }

  .hero-icon i {
    font-size: 35px;
  }

  .balance-card {
    padding: 25px;
  }

  .balance-header {
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }

  .balance-info {
    text-align: center;
  }

  .amount-value {
    font-size: 2rem;
  }

  .balance-actions {
    justify-content: center;
    flex-wrap: wrap;
    gap: 10px;
  }

  .recharge-btn,
  .coupon-btn,
  .send-money-btn {
    padding: 12px 20px;
    font-size: 0.9rem;
  }

  .send-money-modal {
    max-width: 95%;
    margin: 10px;
  }

  .send-money-modal__phone-field .iti {
    width: 100%;
  }

  .transaction-item {
    flex-wrap: wrap;
  }

  .tx-meta {
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #edf2f7;
  }

  .login-card {
    padding: 40px 25px;
  }

  .coupon-modal {
    margin: 10px;
    max-width: calc(100% - 20px);
  }

  .coupon-modal__header {
    padding: 25px;
  }

  .coupon-modal__icon {
    width: 65px;
    height: 65px;
  }

  .coupon-modal__icon i {
    font-size: 28px;
  }

  .coupon-modal__body {
    padding: 25px 20px;
  }

  .coupon-modal__title {
    font-size: 1.3rem;
  }

  .coupon-modal__footer {
    padding: 0 20px 25px;
    flex-direction: column-reverse;
  }

  .coupon-modal__cancel,
  .coupon-modal__submit {
    flex: none;
    width: 100%;
  }

  .tabs-header {
    flex-wrap: wrap;
    gap: 8px;
  }

  .tab-btn {
    flex: 1;
    justify-content: center;
    padding: 10px 15px;
    font-size: 0.9rem;
  }

  .tab-btn span {
    display: none;
  }

  .tab-btn i {
    font-size: 1.2rem;
  }

  .charge-request-item {
    flex-wrap: wrap;
  }

  .req-meta {
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #edf2f7;
  }
}

@media (min-width: 480px) {
  .tab-btn span {
    display: inline;
  }
}
</style>
